import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import { LatLngExpression, LatLngTuple, LeafletEventHandlerFnMap, Marker as LeafletMarker, icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-geosearch/dist/geosearch.css'
import { styles } from '../../organisms/SelectMapDialog/SelectMapDialog-styles'
import Icon from '../../../../blocks.simple/icon/icon'
import translate from '../../../../core/translate'
import Modal from '../../../../blocks.simple/modal/modal'
import { cn } from 'ethcss'
import { isHorizontalMenu } from '../../../../core/helpers/menu'
import {
    GeoSearchLocationEvent,
    GeoSearchMarkerDragEvent,
    LeafletGeoSearchProps,
    OpenStreetMapLocation,
    OpenStreetMapMarker,
    OpenStreetMapProps,
} from './OpenStreetMap-types'
import { TMapPoint } from '../../models/map'

const centerDefault: LatLngTuple = [55.7504461, 37.6174943]

const hasValidCoordinates = (lat: unknown, lng: unknown): boolean => {
    if (lat == null || lng == null || lat === '' || lng === '') {
        return false
    }

    const parsedLat = typeof lat === 'number' ? lat : Number(lat)
    const parsedLng = typeof lng === 'number' ? lng : Number(lng)

    return Number.isFinite(parsedLat) && Number.isFinite(parsedLng)
}

const toLatLngTuple = (lat: unknown, lng: unknown): LatLngTuple | null => {
    if (!hasValidCoordinates(lat, lng)) {
        return null
    }

    return [Number(lat), Number(lng)]
}

const myIcon = icon({
    iconUrl: 'images/displays/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
})

type GeoSearchControlInstance = ReturnType<typeof GeoSearchControl>

const LeafletGeoSearch = ({
    location,
    onChangeLocation,
    onChangePlacemarkGeometry,
    onClearLocation,
    coords,
    changeCoords,
    addressSearchEnabled,
}: LeafletGeoSearchProps) => {
    const [search, setSearch] = useState<GeoSearchControlInstance | null>(null)
    const map = useMap()
    const propsRef = useRef({
        location,
        onChangeLocation,
        onChangePlacemarkGeometry,
        onClearLocation,
        changeCoords,
    })

    useEffect(() => {
        propsRef.current = {
            location,
            onChangeLocation,
            onChangePlacemarkGeometry,
            onClearLocation,
            changeCoords,
        }
    }, [changeCoords, location, onChangeLocation, onChangePlacemarkGeometry, onClearLocation])

    useEffect(() => {
        const initialLocation = propsRef.current.location
        const provider = new OpenStreetMapProvider()
        const searchControl = GeoSearchControl({
            provider,
            searchLabel: translate('enterAddress'),
            showMarker: false,
            marker: {
                icon: myIcon,
                draggable: true,
            },
            classNames: {
                container: styles.searchBlock,
                resetButton: styles.resetBtn,
                form: styles.formBlock,
                input: styles.inputField,
                resultlist: styles.resultBlock,
            },
        })

        setSearch(searchControl)
        map.addControl(searchControl)

        const searchContainer = searchControl.getContainer()

        if (searchContainer && !addressSearchEnabled) {
            searchContainer.style.display = 'none'
        }

        const handleShowLocation = (event: unknown) => {
            const { location: selectedLocation } = (event as unknown) as GeoSearchLocationEvent

            searchControl.searchElement.input.value = selectedLocation.label
            propsRef.current.onChangeLocation({
                name: selectedLocation.label,
                lat: selectedLocation.y,
                lng: selectedLocation.x,
            })
            propsRef.current.onChangePlacemarkGeometry({ lat: selectedLocation.y, lng: selectedLocation.x })
        }

        const handleMarkerDragEnd = (event: unknown) => {
            const { location: markerLocation } = (event as unknown) as GeoSearchMarkerDragEvent
            if (!hasValidCoordinates(markerLocation?.lat, markerLocation?.lng)) {
                return
            }
            propsRef.current.changeCoords(markerLocation)
        }

        const handleReset = () => {
            if (propsRef.current.onClearLocation) {
                propsRef.current.onClearLocation()
            }
        }

        map.on('geosearch/showlocation', handleShowLocation)
        map.on('geosearch/marker/dragend', handleMarkerDragEnd)
        searchControl.resetButton.addEventListener('click', handleReset)

        if (hasValidCoordinates(initialLocation.lat, initialLocation.lng)) {
            if (initialLocation.name) {
                searchControl.searchElement.input.value = initialLocation.name
            }

            const view = toLatLngTuple(initialLocation.lat, initialLocation.lng)
            if (view) {
                map.setView(view, map.getZoom())
            }
        } else if (initialLocation.name) {
            Promise.resolve(searchControl.searchElement.handleSubmit({ query: initialLocation.name })).catch(() => {})
        }

        return () => {
            map.off('geosearch/showlocation', handleShowLocation)
            map.off('geosearch/marker/dragend', handleMarkerDragEnd)
            searchControl.resetButton.removeEventListener('click', handleReset)
            map.removeControl(searchControl)
        }
    }, [addressSearchEnabled, map])

    useEffect(() => {
        if (!search || !coords) return

        const view = toLatLngTuple(coords.lat, coords.lng)
        if (!view) return

        map.setView(view, map.getZoom())
    }, [coords, search, map])

    return null
}

interface DraggableMarkerProps {
    center: LatLngExpression
    onChangePlacemarkGeometry: (point: TMapPoint) => void
    changeCoords: (point: TMapPoint) => void
    draggable?: boolean
    onClick?: (marker: OpenStreetMapMarker) => void
    item?: OpenStreetMapMarker
}

function DraggableMarker({
    center,
    onChangePlacemarkGeometry,
    changeCoords,
    draggable,
    onClick,
    item,
}: DraggableMarkerProps) {
    const [position, setPosition] = useState<LatLngExpression>(center)
    const markerRef = useRef<LeafletMarker | null>(null)
    const eventHandlers = useMemo<LeafletEventHandlerFnMap>(
        () => ({
            dragend() {
                const newPosition = markerRef.current?.getLatLng()

                if (!newPosition) return

                onChangePlacemarkGeometry(newPosition)
                changeCoords(newPosition)
                setPosition(newPosition)
            },
            click() {
                if (onClick && item) {
                    onClick(item)
                }
            },
        }),
        [changeCoords, item, onChangePlacemarkGeometry, onClick]
    )

    useEffect(() => {
        setPosition(center)
    }, [center])

    useEffect(() => {
        const markerElement = markerRef.current?.getElement()

        if (markerElement) {
            markerElement.classList.toggle(styles.activeIcon, Boolean(item?.__view?.selected))
        }
    }, [item])

    return (
        <Marker icon={myIcon} draggable={draggable} eventHandlers={eventHandlers} position={position} ref={markerRef} />
    )
}

const getCenter = (location: OpenStreetMapLocation): LatLngExpression => {
    return toLatLngTuple(location.lat, location.lng) ?? centerDefault
}

const markersAreEqual = (previousMarkers: OpenStreetMapMarker[], nextMarkers: OpenStreetMapMarker[]) => {
    const getCoordinates = ({ lat, lng }: OpenStreetMapMarker) => ({ lat, lng })

    return JSON.stringify(previousMarkers.map(getCoordinates)) === JSON.stringify(nextMarkers.map(getCoordinates))
}

const MapViewportChange = ({ onChange }: { onChange: (center: TMapPoint, distance: number) => void }) => {
    const map = useMap()
    const onChangeRef = useRef(onChange)

    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange])

    useEffect(() => {
        const emitViewportChange = () => {
            const mapCenter = map.getCenter()
            const northEast = map.getBounds().getNorthEast()
            const center = { lat: mapCenter.lat, lng: mapCenter.lng }
            // Leaflet distanceTo is meters; API expects km (same as getDistance / catalog map)
            const distanceKm = Math.max(mapCenter.distanceTo(northEast) / 1000, 1)

            onChangeRef.current(center, distanceKm)
        }

        emitViewportChange()
        map.on('moveend', emitViewportChange)
        map.on('zoomend', emitViewportChange)

        return () => {
            map.off('moveend', emitViewportChange)
            map.off('zoomend', emitViewportChange)
        }
    }, [map])

    return null
}

export const OpenStreetMap = ({
    onClose,
    isOpen,
    inline = false,
    location,
    onChangeLocation,
    onChangePlacemarkGeometry,
    onClearLocation,
    markers,
    onMarkerClick,
    addressSearchEnabled = true,
    onChange,
}: OpenStreetMapProps) => {
    const [coords, setCoords] = useState<TMapPoint | null>(null)
    const [markersList, setMarkers] = useState<OpenStreetMapMarker[]>(markers ?? [])

    useEffect(() => {
        if (!markers) return

        setMarkers((currentMarkers) => (markersAreEqual(currentMarkers, markers) ? currentMarkers : []))

        const timer = window.setTimeout(() => {
            setMarkers(markers)
        }, 15)

        return () => window.clearTimeout(timer)
    }, [markers])

    const changeCoords = useCallback((point: TMapPoint) => {
        setCoords(point)
    }, [])

    const clearLocation = useCallback(() => {
        setCoords(null)

        if (onClearLocation) {
            onClearLocation()
        }
    }, [onClearLocation])

    const renderMarkers = (markerList: OpenStreetMapMarker[]) =>
        markerList
            .filter((marker) => hasValidCoordinates(marker.lat, marker.lng))
            .map((marker) => (
                <DraggableMarker
                    key={marker.id ?? `${marker.lat}_${marker.lng}`}
                    center={getCenter({ lat: marker.lat, lng: marker.lng })}
                    onChangePlacemarkGeometry={() => {}}
                    changeCoords={() => {}}
                    onClick={onMarkerClick}
                    item={marker}
                />
            ))

    const renderLocationMap = () => (
        <MapContainer
            center={getCenter(location)}
            zoom={10}
            style={{ height: '100%' }}
            className={cn(styles.openMapWrapper, inline ? styles.inlineMapWrapper : '')}
        >
            <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LeafletGeoSearch
                location={location}
                onChangeLocation={onChangeLocation}
                onChangePlacemarkGeometry={onChangePlacemarkGeometry}
                onClearLocation={clearLocation}
                coords={coords}
                changeCoords={changeCoords}
                addressSearchEnabled={addressSearchEnabled}
            />
            <DraggableMarker
                center={getCenter(location)}
                onChangePlacemarkGeometry={onChangePlacemarkGeometry}
                changeCoords={changeCoords}
                draggable={true}
            />
        </MapContainer>
    )

    return (
        <>
            {!markers ? (
                inline ? (
                    renderLocationMap()
                ) : (
                    <Modal open={isOpen} showCloseIcon={false} mod="content">
                        <div className={styles.selectMapDialog__close} onClick={onClose}>
                            <Icon type="minimize" color="black" size={28} tooltip={{ title: translate('collapse') }} />
                        </div>
                        {renderLocationMap()}
                    </Modal>
                )
            ) : (
                <MapContainer
                    center={getCenter(location)}
                    zoom={10}
                    style={{ height: '100%' }}
                    className={cn(
                        styles.openMapWrapper,
                        styles.zeroIndex,
                        isHorizontalMenu() ? styles.mapCorrection_x : ''
                    )}
                >
                    <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {onChange ? <MapViewportChange onChange={onChange} /> : null}
                    {renderMarkers(markersList)}
                </MapContainer>
            )}
        </>
    )
}
